import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import SaveCancelGroup from '@/components/ui/custom/buttons/SaveCancelGroup';
import { IconByStatus } from '@/components/ui/custom/dataDisplay/IconByUserStatus';
import { RenderRewards } from '@/components/ui/custom/dataDisplay/RewardsView';
import Text from '@/components/ui/custom/dataDisplay/Text';
import DateInput from '@/components/ui/custom/dataInput/DateInput';
import InfiniteSearchInput from '@/components/ui/custom/dataInput/InfiniteSearchInput';
import PasswordAndConfirmInput from '@/components/ui/custom/dataInput/PasswordAndConfirmInput';
import PlacesAutoCompleteInput from '@/components/ui/custom/dataInput/PlacesAutoCompleteInput';
import Input from '@/components/ui/custom/dataInput/TextInput';
import UsernameInput from '@/components/ui/custom/dataInput/UsernameInput';
import EnrollmentTag from '@/components/enrollment/EnrollmentTag';
import { HistoricTagGroup } from '@/components/historic/HistoricCourseTag';
import useAuth from '@/data/hook/useAuth';
import useInfiniteService from '@/data/hook/useInfiniteService';
import {
  simplifiedCourseProgressesQueryKeys,
  studentsQuerykeys,
  unitsQueryKeys,
  userProfilesQueryKeys,
} from '@/data/services/querykeys';
import { updateUserProfile } from '@/data/services/userProfileServices';
import { createUser, updateUser } from '@/data/services/userServices';
import { ApiError } from '@/models/Errors';
import Rewards from '@/models/Rewards';
import { ShortEnrollment, Student } from '@/models/Student';
import User, { UserForm as UserFormType, UserTypeEnum } from '@/models/User';
import UserProfile from '@/models/UserProfile';
import alert from '@/utils/shared/alert';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getAuthorizedUnits } from '@/utils/getAuthorizedUnits';
import {
  extractCityAndState,
  returnLocationString,
} from '@/utils/placesLocationUtils';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { every, isEmpty, isEqual, some } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

interface UserFormProps {
  user?: Student | User;
  profile?: UserProfile;
  formMode?: 'create' | 'edit';
  disabled?: boolean;
  className?: string;
  onCancel?(): void;
  rewards?: Rewards;
  isLoadingRewards?: boolean;
}

export default function StudentForm(props: UserFormProps) {
  const {
    user = {} as Student,
    profile = {} as UserProfile,
    disabled,
    formMode = 'edit',
    className,
    onCancel,
    rewards,
    isLoadingRewards,
  } = props;

  const { user: authUser } = useAuth();
  const [hasAlter, setHasAlter] = useState<boolean>(false);

  const { results: progress, isInitialLoading: isLoadingProgress } =
    useInfiniteService({
      ...simplifiedCourseProgressesQueryKeys.list(user.id)._ctx.infinity,
      enabled: formMode === 'edit' && !!user,
    });

  const { t } = useTranslation('translation', {
    keyPrefix: 'manageStudent.addUser',
  });

  const { t: tWarning } = useTranslation('translation', {
    keyPrefix: 'manageStudent',
  });

  const { t: tStatus } = useTranslation('translation', {
    keyPrefix: 'manageStudent.status',
  });

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, defaultValues },
  } = useForm<UserFormType>({
    defaultValues: {
      ...user,
      ...profile,
      dateOfBirth: profile?.dateOfBirth,
      location: returnLocationString(profile?.city, profile?.state),
    },
  });

  const blockChanges = some(errors, error => !!error?.message);

  const onEdit = async ({
    firstName,
    lastName,
    username,
    userType,
    location,
    dateOfBirth,
    phoneNumber,
    email,
  }: UserFormType) => {
    if (!user) throw new Error('User not found');
    const { city, state } = extractCityAndState(location);

    const parcialProfile = buildChangedObject<UserProfile>(profile, {
      city,
      state,
      phoneNumber,
      dateOfBirth: dateOfBirth
        ? moment(dateOfBirth).format('YYYY-MM-DD')
        : undefined,
    });

    const parcialUser = buildChangedObject<User>(user, {
      firstName,
      lastName,
      username,
      userType,
      email,
    });

    if (!every(parcialUser, isEmpty)) {
      await updateUser(user.id, parcialUser);
    }

    if (!every(parcialProfile, isEmpty)) {
      await updateUserProfile(profile.id, parcialProfile);
    }
  };

  const { mutate: edit, isLoading: isUpdating } = useMutation(onEdit, {
    onSuccess: () => {
      alert.success(t('successEditUser'));
      queryClient.invalidateQueries(userProfilesQueryKeys.get(user.username));
      queryClient.invalidateQueries(studentsQuerykeys.list._def);
      onCancel?.();
    },
    onError: (error: any) => {
      const apiError = new ApiError(error);

      alert.error(apiError.getErrorMessage());
    },
  });

  const onCreate = async ({
    location,
    dateOfBirth,
    phoneNumber,
    ...user
  }: UserFormType) => {
    const { city, state } = extractCityAndState(location);

    await createUser({
      ...user,
      userType: UserTypeEnum.STUDENT,
      profile: {
        city,
        state,
        phoneNumber: phoneNumber || undefined,
        dateOfBirth: dateOfBirth
          ? moment(dateOfBirth).format('YYYY-MM-DD')
          : undefined,
      },
    });
  };

  const { mutate: create, isLoading: isCreating } = useMutation(onCreate, {
    onSuccess: () => {
      alert.success(t('successCreateUser'));
      queryClient.invalidateQueries(studentsQuerykeys.list._def);
      onCancel?.();
    },
    onError: (error: any) => {
      const apiError = new ApiError(error);
      alert.error(apiError.getErrorMessage());
    },
  });

  const onSubmit = async (data: UserFormType) => {
    if (formMode === 'create') {
      create(data);
    } else {
      edit(data);
    }
    setHasAlter(false);
  };

  const onReset = () => {
    reset({
      ...profile,
      ...user,
      dateOfBirth: profile?.dateOfBirth,
      location: returnLocationString(profile?.city, profile?.state),
    });
    setHasAlter(false);
    onCancel?.();
  };

  useEffect(() => {
    const subscribe = watch(value =>
      setHasAlter(!isEqual(value, defaultValues)),
    );
    return () => {
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={twMerge('flex flex-col gap-4 pb-2', className)}
    >
      <ComponentGuard roles={[UserTypeEnum.TEACHER, UserTypeEnum.UNIT_ADMIN]}>
        <div
          role="alert"
          className="alert bg-warning-content text-warning font-500"
        >
          <ExclamationTriangleIcon className="w-6 h-6" />
          <Text text={tWarning('studentWarning')} />
        </div>
      </ComponentGuard>
      <ConditionalRenderer condition={formMode === 'create'}>
        <Controller
          name="unitsIds"
          control={control}
          render={({ field: { onChange }, fieldState: { error } }) => (
            <InfiniteSearchInput
              service={unitsQueryKeys.list}
              displayName={unit => unit.name}
              onSelect={({ id }) => {
                const units = [id];
                onChange(units);
              }}
              filters={{
                idIn: getAuthorizedUnits(authUser)?.toString(),
                isActive: true,
              }}
              input={{
                label: t('unit'),
                errorLabelText: error?.message,
                labelPosition: 'left',
                disabled,
                testId: 'selectUnit',
                className: { base: 'font-500 text-16' },
              }}
              onDeselect={() => onChange(null)}
            />
          )}
          rules={{
            required: formMode === 'create' ? t('errorMessageUnit') : undefined,
          }}
        />
      </ConditionalRenderer>
      <Input
        label={t('name')}
        testId="name"
        register={register('firstName', {
          required: t('errorMessageName'),
        })}
        disabled={disabled}
        errorLabelText={errors.firstName?.message}
        labelPosition="left"
        className={{ base: 'font-500 text-16' }}
      />
      <Input
        label={t('lastName')}
        testId="lastName"
        register={register('lastName', {
          required: t('errorMessageLastName'),
        })}
        disabled={disabled}
        errorLabelText={errors.lastName?.message}
        labelPosition="left"
        className={{ base: 'font-500 text-16' }}
      />

      <Controller
        control={control}
        name="dateOfBirth"
        render={({ field: { onChange, value } }) => (
          <DateInput
            label={t('birth')}
            labelPosition="left"
            testId="birth"
            value={value}
            onDateChange={onChange}
            disabled={disabled}
            className={{ base: 'font-500 text-16' }}
          />
        )}
      />

      <UsernameInput
        control={control}
        user={user ?? undefined}
        disabled={disabled}
        labelPosition="left"
        required
        className={{ base: 'font-500 text-16' }}
      />

      <Input
        label={t('email')}
        testId="email"
        register={register('email', {
          required: t('errorMessageEmail'),
        })}
        type="email"
        disabled={disabled}
        errorLabelText={errors.email?.message}
        labelPosition="left"
        className={{ base: 'font-500 text-16' }}
      />

      <ComponentGuard
        roles={[UserTypeEnum.UNIT_ADMIN, UserTypeEnum.SUPER_ADMIN]}
      >
        <Input
          label={t('phoneNumber')}
          testId="phoneNumber"
          register={register('phoneNumber')}
          disabled={disabled}
          errorLabelText={errors.phoneNumber?.message}
          labelPosition="left"
          className={{ base: 'font-500 text-16' }}
          type="phone"
        />
      </ComponentGuard>

      <PlacesAutoCompleteInput
        disabled={disabled}
        testId="city"
        placeholder={disabled ? '' : t('selectCity')}
        label={t('city')}
        labelPosition="left"
        control={control}
        name="location"
        className={{ base: 'font-500 text-16' }}
      />
      <ConditionalRenderer condition={formMode === 'create'}>
        <PasswordAndConfirmInput
          control={control}
          disabled={disabled}
          labelPosition="left"
          className={{ base: 'font-500 text-16' }}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={formMode === 'edit'}>
        <RenderKlass
          enrollments={
            'klasses' in user ? (user.klasses as ShortEnrollment[]) : undefined
          }
        />

        <div className="flex gap-2">
          <Text
            text={t('status')}
            className="font-500 text-sm self-center shrink-0 text-left w-28"
          />
          <span className="flex gap-2 items-center w-full px-3">
            <IconByStatus status={user.status} />
            {tStatus(user.status)}
          </span>
        </div>

        <RenderRewards
          rewardsData={rewards}
          isFetchingRewards={isLoadingRewards}
        />
        <HistoricTagGroup progress={progress} isLoading={isLoadingProgress} />
      </ConditionalRenderer>

      <ConditionalRenderer condition={!disabled}>
        <SaveCancelGroup
          loading={isUpdating || isCreating}
          cancel={{
            onClick: onReset,
          }}
          save={{
            disabled: !hasAlter || blockChanges,
          }}
        />
      </ConditionalRenderer>
    </form>
  );
}

type RenderKlassProps = {
  enrollments?: ShortEnrollment[];
};

const RenderKlass = ({ enrollments }: RenderKlassProps) => {
  const enrollmentList = enrollments && enrollments.length ? enrollments : [];

  const { t } = useTranslation('translation', {
    keyPrefix: 'manageStudent.addUser',
  });
  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 w-full">
        <Text
          text={t('klass')}
          className="text-base-content font-500 text-sm self-start text-left w-28"
        />

        <ul className="flex flex-col gap-2 grow">
          {enrollmentList.map(enrollment => (
            <li
              className="flex gap-2 w-fit flex-wrap relative"
              key={enrollment.name}
            >
              <Text
                text={enrollment.name}
                className="flex gap-2 items-center w-fit px-3"
              />

              <EnrollmentTag
                status={enrollment.status}
                className="text-[0.7rem] absolute left-full -bottom-0.5"
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
